import Heading from 'components/Luxkit/Typography/Heading'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import React, { useEffect, useMemo, useRef } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import ExperienceTileSkeletonLoader from '../ExperienceTiles/ExperienceTileSkeletonLoader'
import { fillArray, take } from 'lib/array/arrayUtils'
import ExperienceTile from '../ExperienceTiles/ExperienceTile'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { fetchHeroesByRegion } from 'actions/ExperienceActions'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { useAppSelector } from 'hooks/reduxHooks'
import useExperiences from 'hooks/Experiences/useExperiences'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'

const TOP_OFFERS_LIMIT = 3
const ListCount = fillArray(TOP_OFFERS_LIMIT)

interface Props {
  place: App.Place;
  showHeroes?: boolean;
}

function ExperiencesPageFeaturedCarousel(props: Props) {
  const { place, showHeroes } = props

  const scrollElementRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()

  const fetchingRegionHeroes = useAppSelector((state) => state.experience.fetchingRegionHeroes)
  const heroIds = useAppSelector((state) => state.experience.experienceRegionHeroes)
  const [heroExperiences, heroFetching] = useExperiences(heroIds)

  useEffect(() => {
    dispatch(fetchHeroesByRegion())
  }, [dispatch])

  useEffect(() => {
    // if we change our place, we're looking at a "new list"
    // so take the carousel back to the start
    if (scrollElementRef.current) {
      scrollElementRef.current.scrollLeft = 0
    }
  }, [place.id])

  const [list, fallbackExperiences] = useExperienceSearch({
    placeId: place.id,
    limit: TOP_OFFERS_LIMIT,
    sortBy: 'recommended',
  })

  const listFetching = heroFetching || fetchingRegionHeroes || list?.fetching

  const expViews = useMemo(() => {
    let toViews = fallbackExperiences
    if (showHeroes) {
      toViews = [...heroExperiences, ...fallbackExperiences]
    }
    // merge if experiencesRegionHeroes is less than 3
    return take(toViews, TOP_OFFERS_LIMIT).map(exp => exp ? mapExperienceToBookingView(exp) : undefined)
  }, [fallbackExperiences, showHeroes, heroExperiences])

  return (
    <VerticalSpacer gap={24}>
      <Heading variant="heading3">Our top {TOP_OFFERS_LIMIT} experiences</Heading>
      <div>
        <CardCarousel pageSize={3} width="75%" ref={scrollElementRef}>
          {listFetching && ListCount.map((_, index) => <ExperienceTileSkeletonLoader key={index} />)}
          {!listFetching && expViews.map((expView, index) => <React.Fragment key={expView?.id ?? index}>
            {!expView && <ExperienceTileSkeletonLoader />}
            {expView && <ExperienceTile
              key={expView.id}
              to={getExperienceOfferPageURL(expView.originalExperience)}
              experience={expView}
            />}
          </React.Fragment>,
          )}
        </CardCarousel>
      </div>
    </VerticalSpacer>

  )
}

export default ExperiencesPageFeaturedCarousel
